import { Link } from 'react-router-dom';
import { useCompany } from '../App';
import { api } from '../lib/api';
import { useAsync } from '../lib/useAsync';
import { Card, DataTable, ErrorBanner, Spinner, StatTile, Badge } from '../components/ui';
// Absolute hrefs: these happen to resolve correctly only because Dashboard is
// the index route. A relative target inside the company splat accumulates path
// segments — see the comment in lib/paths.js.
import { companyBase } from '../lib/paths';
import { roleAtLeast } from '../lib/roles';
import OwnershipDonut from '../components/charts/OwnershipDonut';
import { date, enumLabel, percent, pricePerShare, securityType, shares } from '../lib/format';

export default function Dashboard() {
  const { role } = useCompany();
  // EMPLOYEE and INVESTOR are self-scoped: GET /cap-table has no per-caller
  // scoping and requires AUDITOR, so this Overview would otherwise be their
  // one unconditionally-fetched page and would 403 for them on every login.
  return roleAtLeast(role, 'AUDITOR') ? <CompanyOverview /> : <MyHoldings />;
}

function CompanyOverview() {
  const { companyId, company, role } = useCompany();
  const table = useAsync(() => api.capTable(companyId), [companyId]);
  // LEGAL-gated on the API itself (an election names a specific stakeholder's
  // grant, the same cross-holder detail GET /stakeholders restricts) — an
  // AUDITOR viewing this same page must not have this fetch 403 underneath them.
  const canSeeElections = roleAtLeast(role, 'LEGAL');
  const elections = useAsync(
    () => (canSeeElections ? api.compliance83bWarnings(companyId) : Promise.resolve([])),
    [companyId, canSeeElections]
  );

  if (table.loading) return <Spinner label="Deriving the cap table" />;
  if (table.error) return <ErrorBanner error={table.error} onRetry={table.reload} />;

  const t = table.data.totals;
  const summary = company.summary ?? {};
  const overGranted = table.data.plans?.filter((p) => p.overGranted) ?? [];

  return (
    <div className="stack">
      <div className="page-head">
        <div>
          <h1>Overview</h1>
          <p>
            Every figure here is derived from the append-only ledger at read time — nothing
            below is a stored total, so it cannot drift from the underlying share counts.
          </p>
        </div>
      </div>

      {overGranted.length ? (
        <div className="banner banner-warning" role="alert">
          <div>
            <strong>An equity plan is over-granted.</strong>
            <div className="banner-detail">
              {overGranted
                .map((p) => `${p.name}: ${shares(p.overGranted)} shares beyond authorization`)
                .join(' · ')}
              . This normally means a board approval was recorded late — add a pool
              adjustment under Classes &amp; plans.
            </div>
          </div>
        </div>
      ) : null}

      {elections.data?.length ? (
        <div className="banner banner-warning" role="alert">
          <div>
            <strong>
              {elections.data.length} 83(b) election{elections.data.length === 1 ? '' : 's'} outstanding.
            </strong>
            <div className="banner-detail">
              An early exercise has 30 calendar days from exercise to file an 83(b) election with
              the IRS — there is no extension. Mark one filed once the recipient has proof of
              mailing.
            </div>
            <ul className="banner-issues">
              {elections.data.map((e) => (
                <li key={e.exerciseId}>
                  {e.label} ({e.stakeholderName}) —{' '}
                  {e.status === 'MISSED'
                    ? `election was due ${e.deadline} and appears unfiled`
                    : `due ${e.deadline} (${e.daysRemaining} day${e.daysRemaining === 1 ? '' : 's'} left)`}
                  {' — '}
                  <button
                    type="button"
                    className="link-button"
                    onClick={async () => {
                      await api.markExercise83bFiled(companyId, e.exerciseId);
                      elections.reload();
                    }}
                  >
                    Mark filed
                  </button>
                </li>
              ))}
            </ul>
          </div>
        </div>
      ) : null}

      <div className="grid grid-stats">
        <StatTile label="Fully diluted" value={shares(t.fullyDiluted)} sub="incl. pool & awards" />
        <StatTile label="Outstanding" value={shares(t.outstanding)} sub="stock only — what votes" />
        <StatTile label="As converted" value={shares(t.asConverted)} sub="preferred → common" />
        <StatTile
          label="Options outstanding"
          value={shares(t.optionsOutstanding)}
          sub={`${shares(t.optionsVested)} vested`}
        />
        <StatTile
          label="Pool unallocated"
          value={shares(t.poolUnallocated)}
          sub={`of ${shares(t.poolAuthorized)} authorized`}
        />
        <StatTile
          label="Latest 409A"
          value={summary.latest_409a ? pricePerShare(summary.latest_409a) : '—'}
          sub={summary.latest_409a ? 'per share' : 'none recorded'}
        />
      </div>

      <div className="grid grid-2">
        <Card
          title="Ownership, fully diluted"
          actions={<Link to={companyBase(companyId, 'cap-table')}>Full cap table →</Link>}
        >
          <OwnershipDonut
            stakeholders={table.data.stakeholders}
            total={t.fullyDiluted}
            basis="fullyDiluted"
          />
        </Card>

        <Card title="Share classes" padded={false} actions={<Link to={companyBase(companyId, 'structure')}>Manage →</Link>}>
          <DataTable
            rowKey={(r) => r.shareClassId}
            empty="No share classes yet. Create one before issuing stock."
            columns={[
              {
                key: 'name',
                header: 'Class',
                render: (r) => (
                  <span className="primary-cell">
                    {r.name} <Badge tone="neutral">{enumLabel(r.classType)}</Badge>
                  </span>
                ),
              },
              { key: 'outstanding', header: 'Outstanding', align: 'right', numeric: true, render: (r) => shares(r.outstanding) },
              { key: 'available', header: 'Available', align: 'right', numeric: true, render: (r) => shares(r.available) },
              { key: 'pct', header: '% FD', align: 'right', numeric: true, render: (r) => percent(r.percentFullyDiluted) },
            ]}
            rows={table.data.shareClasses}
          />
        </Card>
      </div>

      {table.data.convertibles?.length ? (
        <Card
          title={`Outstanding convertibles (${table.data.convertibles.length})`}
          padded={false}
        >
          <div style={{ padding: '10px 16px 0', fontSize: 12, color: 'var(--text-secondary)' }}>
            Excluded from fully diluted above — a SAFE has no share count until a priced round
            sets its conversion price. Model one under Rounds to see the effect.
          </div>
          <DataTable
            rowKey={(r) => r.securityId}
            columns={[
              { key: 'stakeholderName', header: 'Holder', render: (r) => <span className="primary-cell">{r.stakeholderName}</span> },
              { key: 'principal', header: 'Principal', align: 'right', numeric: true, render: (r) => `$${r.principal}` },
              { key: 'valuationCap', header: 'Cap', align: 'right', numeric: true, render: (r) => (r.valuationCap ? `$${r.valuationCap}` : '—') },
              { key: 'discountRate', header: 'Discount', align: 'right', numeric: true, render: (r) => (r.discountRate ? percent(r.discountRate) : '—') },
              { key: 'postMoney', header: 'Basis', render: (r) => (r.postMoney ? 'Post-money' : 'Pre-money') },
            ]}
            rows={table.data.convertibles}
          />
        </Card>
      ) : null}
    </div>
  );
}

/**
 * EMPLOYEE/INVESTOR landing view: their own position only, from
 * GET /cap-table/my-position — the endpoint the API has always exposed for
 * this, which nothing in the UI called until this page needed one.
 */
function MyHoldings() {
  const { companyId } = useCompany();
  const position = useAsync(() => api.myPosition(companyId), [companyId]);

  if (position.loading) return <Spinner label="Loading your holdings" />;
  if (position.error) {
    // 404 here means "no stakeholder record" — a real, expected state for a
    // portal account granted access before Legal recorded their grant.
    if (position.error.status === 404) {
      return (
        <div className="stack">
          <h1>Overview</h1>
          <Card>You have no holdings on record yet. Check back once your grant is recorded.</Card>
        </div>
      );
    }
    return <ErrorBanner error={position.error} onRetry={position.reload} />;
  }

  const p = position.data.stakeholder;

  return (
    <div className="stack">
      <div className="page-head">
        <div>
          <h1>Overview</h1>
          <p>Your holdings in this company, derived from the register at read time.</p>
        </div>
      </div>

      <div className="grid grid-stats">
        <StatTile label="Fully diluted shares" value={shares(p.fullyDiluted)} sub="all your securities, as-converted" />
        <StatTile label="Vested" value={shares(p.vestedAwards)} sub="options and RSUs vested to date" />
        <StatTile label="% of company" value={percent(p.percentFullyDiluted)} sub="fully diluted basis" />
      </div>

      <Card title="Your securities" padded={false}>
        <DataTable
          rowKey={(r) => r.securityId}
          empty="No securities on record."
          columns={[
            { key: 'label', header: 'Certificate', render: (r) => <span className="primary-cell">{r.label}</span> },
            { key: 'type', header: 'Type', render: (r) => <Badge tone="neutral">{securityType(r.securityType)}</Badge> },
            { key: 'shareClass', header: 'Class', render: (r) => r.shareClass ?? '—' },
            { key: 'quantity', header: 'Quantity', align: 'right', numeric: true, render: (r) => shares(r.quantity) },
            { key: 'vested', header: 'Vested', align: 'right', numeric: true, render: (r) => shares(r.vested) },
            { key: 'strike', header: 'Strike', align: 'right', numeric: true, render: (r) => (r.strikePrice ? pricePerShare(r.strikePrice) : '—') },
            { key: 'issued', header: 'Issued', render: (r) => date(r.issueDate) },
          ]}
          rows={p.holdings}
        />
      </Card>
    </div>
  );
}
